import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserSquare, CircleCheckBig, UserPlus, Wallet, Star } from 'lucide-react';
import api from '@/services/apiClient';
import useAuthStore from '@/store/authStore';
import PageHeader from '@/components/ui/PageHeader';
import Spinner from '@/components/ui/Spinner';
import { fmtMoney, fmtNum } from '@/lib/enums';
import styles from './Dashboard.module.css';

function greeting(t) {
  const h = new Date().getHours();
  if (h < 12) return t('dashboard.goodMorning');
  if (h < 18) return t('dashboard.goodAfternoon');
  return t('dashboard.goodEvening');
}

function firstName(name) {
  return (name || '').trim().split(/\s+/)[0] || '';
}

function StatCard({ label, value, tone = 'hi', delta, deltaTone = 'accent', icon }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statHead}>
        <span className={styles.statLabel}>{label}</span>
        {icon && <span className={styles.statIcon}>{icon}</span>}
      </div>
      <div className={`${styles.statValue} ${tone !== 'hi' ? styles[tone] : ''}`}>{value}</div>
      {delta && <div className={`${styles.statDelta} ${deltaTone === 'muted' ? styles.muted : ''}`}>{delta}</div>}
    </div>
  );
}

export default function TrainerDashboard() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/metrics/trainer')
      .then((res) => setMetrics(res.data || res))
      .catch(() => setMetrics({}))
      .finally(() => setLoading(false));
  }, []);

  const name = firstName(user?.name);

  return (
    <>
      <PageHeader kicker="Movez · Personal" title={`${greeting(t)}${name ? `, ${name}` : ''}`} />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}><Spinner /></div>
      ) : (
        <div className={styles.statGrid}>
          <StatCard
            label={t('dashboard.activeStudents')}
            value={fmtNum(metrics?.activeStudents ?? 0)}
            tone="accent"
            delta={metrics?.newStudents ? t('dashboard.newThisMonth', { count: metrics.newStudents }) : t('dashboard.noneThisMonth')}
            deltaTone={metrics?.newStudents ? 'accent' : 'muted'}
            icon={<UserSquare size={18} strokeWidth={2} />}
          />
          <StatCard
            label={t('dashboard.completedWorkouts')}
            value={fmtNum(metrics?.completedWorkouts ?? 0)}
            delta={t('dashboard.last30Days')}
            deltaTone="muted"
            icon={<CircleCheckBig size={18} strokeWidth={2} />}
          />
          <StatCard
            label={t('dashboard.newStudents')}
            value={fmtNum(metrics?.newStudents ?? 0)}
            delta={t('dashboard.thisMonth')}
            deltaTone="muted"
            icon={<UserPlus size={18} strokeWidth={2} />}
          />
          <StatCard
            label={t('dashboard.monthlyRevenue')}
            value={metrics?.revenue == null ? '—' : fmtMoney(metrics.revenue)}
            tone="accent"
            delta={t('dashboard.marketplacePlans')}
            deltaTone="muted"
            icon={<Wallet size={18} strokeWidth={2} />}
          />
          <StatCard
            label={t('dashboard.rating')}
            value={metrics?.ratingAvg ? Number(metrics.ratingAvg).toFixed(1) : '—'}
            tone="recovery"
            delta={t('dashboard.studentsAvg')}
            deltaTone="muted"
            icon={<Star size={18} strokeWidth={2} />}
          />
        </div>
      )}
    </>
  );
}
